import type { WizardFormData, WorldRegion } from '../../../../types/wizard';
import { useTranslation } from 'react-i18next';
import StepCard from '../../../../components/wizard/StepCard';
import SelectableChip from '../../../../components/wizard/SelectableChip';

interface Step3RegionsProps {
  formData: WizardFormData;
  updateFormData: (data: Partial<WizardFormData>) => void;
  errors: Record<string, string>;
}

const Step3Regions = ({ formData, updateFormData, errors }: Step3RegionsProps) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const regions: Array<{ value: WorldRegion; label: string; icon: string }> = [
    { value: 'Europa', label: isEnglish ? 'Europe' : 'Europa', icon: 'ri-map-pin-line' },
    { value: 'América do Norte', label: isEnglish ? 'North America' : 'América do Norte', icon: 'ri-map-pin-line' },
    { value: 'América do Sul', label: isEnglish ? 'South America' : 'América do Sul', icon: 'ri-map-pin-line' },
    { value: 'Ásia', label: isEnglish ? 'Asia' : 'Ásia', icon: 'ri-map-pin-line' },
    { value: 'África', label: isEnglish ? 'Africa' : 'África', icon: 'ri-map-pin-line' },
    { value: 'Oceania', label: 'Oceania', icon: 'ri-map-pin-line' },
    { value: 'Oriente Médio', label: isEnglish ? 'Middle East' : 'Oriente Médio', icon: 'ri-map-pin-line' },
    { value: 'Caribe', label: isEnglish ? 'Caribbean' : 'Caribe', icon: 'ri-map-pin-line' },
  ];

  const toggleRegion = (region: WorldRegion) => {
    const currentRegions = formData.step3_regions || [];
    const isSelected = currentRegions.includes(region);

    const newRegions = isSelected
      ? currentRegions.filter((r) => r !== region)
      : [...currentRegions, region];

    updateFormData({ step3_regions: newRegions });
  };

  return (
    <div className="space-y-8">
      <StepCard>
        <div className="mb-6">
          <label className="block text-white text-lg font-medium mb-2">
            {isEnglish ? 'Which world regions interest you?' : 'Quais regiões do mundo te interessam?'} <span className="text-[#FFD700]">*</span>
          </label>
          <p className="text-white/90 text-sm mb-4">
            {isEnglish
              ? 'Select one or more regions you would like to explore'
              : 'Selecione uma ou mais regiões que você gostaria de explorar'}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {regions.map((region) => (
            <SelectableChip
              key={region.value}
              label={region.label}
              icon={region.icon}
              selected={formData.step3_regions?.includes(region.value) || false}
              onClick={() => toggleRegion(region.value)}
            />
          ))}
        </div>

        {formData.step3_regions && formData.step3_regions.length > 0 && (
          <div className="mt-4 p-4 bg-[#FFD700]/10 border border-[#FFD700]/20 rounded-lg">
            <p className="text-white/90 text-sm">
              <i className="ri-information-line text-[#FFD700] mr-2"></i>
              {formData.step3_regions.length} {formData.step3_regions.length === 1
                ? (isEnglish ? 'region selected' : 'região selecionada')
                : (isEnglish ? 'regions selected' : 'regiões selecionadas')}
            </p>
          </div>
        )}

        {(errors.step3_regions || errors.regions) && (
          <p className="text-red-400 text-sm mt-4 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errors.step3_regions || errors.regions}
          </p>
        )}
      </StepCard>
    </div>
  );
};

export default Step3Regions;
